import React, { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { AiDraft, AiMode, ConversationDetail, ConversationListItem, Id } from "../types";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { formatDateTime } from "../lib/format";

type Scope = "mine" | "unassigned" | "all";

const MODES: { key: AiMode; label: string }[] = [
  { key: "auto", label: "Auto IA" },
  { key: "assist", label: "Copilot" },
  { key: "human", label: "Humano" },
];

export default function Inbox() {
  const { user, isAdminLike } = useAuth();
  const { show } = useToast();
  const isDevToolVisible = user ? ["admin", "director"].includes(user.role_code) : false;

  const [scope, setScope] = useState<Scope>("mine");
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState<Id | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const [composeText, setComposeText] = useState("");
  const [sending, setSending] = useState(false);
  const [devPhone, setDevPhone] = useState("");
  const [devText, setDevText] = useState("");

  const loadConversations = useCallback(async () => {
    setLoadingList(true);
    try {
      const rows = await api.getConversations(scope);
      setConversations(rows);
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudieron cargar las conversaciones.", "error");
    } finally {
      setLoadingList(false);
    }
  }, [scope, show]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const loadDetail = useCallback(
    async (id: Id) => {
      setLoadingDetail(true);
      try {
        const [d, aiDraft] = await Promise.all([api.getConversation(id), api.getAiDraft(id).catch(() => null)]);
        setDetail(d);
        setDraft(aiDraft);
      } catch (e) {
        show("No se pudo cargar la conversación.", "error");
      } finally {
        setLoadingDetail(false);
      }
    },
    [show]
  );

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else {
      setDetail(null);
      setDraft(null);
    }
  }, [selectedId, loadDetail]);

  useEffect(() => {
    if (detail?.phone) setDevPhone(detail.phone);
  }, [detail?.phone]);

  function selectConversation(id: Id) {
    setSelectedId(id);
    setComposeText("");
  }

  async function refreshAll() {
    await Promise.all([loadConversations(), selectedId ? loadDetail(selectedId) : Promise.resolve()]);
  }

  async function sendMessage() {
    if (!detail || !composeText.trim()) return;
    setSending(true);
    try {
      await api.sendConversationMessage(detail.id, composeText.trim());
      setComposeText("");
      await refreshAll();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo enviar el mensaje.", "error");
    } finally {
      setSending(false);
    }
  }

  function useDraft() {
    if (draft) setComposeText(draft.text || "");
  }

  async function changeMode(mode: AiMode) {
    if (!detail) return;
    try {
      const updated = await api.setConversationMode(detail.id, mode);
      setDetail((d) => (d ? { ...d, ai_mode: updated.ai_mode } : d));
      loadConversations();
      show("Modo de conversación actualizado.", "success");
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo cambiar el modo.", "error");
    }
  }

  async function acceptHandoff() {
    if (!detail) return;
    try {
      await api.acceptHandoff(detail.id);
      show("Handoff aceptado. Ahora tenés el control de la conversación.", "success");
      await refreshAll();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo aceptar el handoff.", "error");
    }
  }

  async function simulateInbound() {
    if (!devPhone.trim() || !devText.trim()) {
      show("Completá teléfono y texto para simular el mensaje.", "error");
      return;
    }
    try {
      await api.simulateInbound({ phone: devPhone.trim(), text: devText.trim() });
      show("Mensaje entrante simulado.", "success");
      setDevText("");
      await refreshAll();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo simular el mensaje entrante.", "error");
    }
  }

  const scopes: { key: Scope; label: string }[] = [
    { key: "mine", label: "Mías" },
    { key: "unassigned", label: "Sin asignar" },
    ...(isAdminLike ? [{ key: "all" as Scope, label: "Todas" }] : []),
  ];

  return (
    <div className="inbox-page">
      <div className="kanban-header">
        <div>
          <h1>Inbox</h1>
          <p className="meta">Conversaciones omnicanal con IA, copiloto y handoff a humano.</p>
        </div>
      </div>

      <div className="inbox-grid">
        {/* Left: conversation list */}
        <div className="inbox-col">
          <div className="inbox-tabs">
            {scopes.map((s) => (
              <button key={s.key} className={`tab ${scope === s.key ? "active" : ""}`} onClick={() => setScope(s.key)}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="inbox-scroll">
            {loadingList && <div className="loading-strip">Cargando...</div>}
            {!loadingList && conversations.length === 0 && <div className="col-empty">Sin conversaciones.</div>}
            {conversations.map((c) => (
              <div
                key={c.id}
                className={`conv-item ${String(c.id) === String(selectedId) ? "active" : ""}`}
                onClick={() => selectConversation(c.id)}
              >
                <div className="name">
                  <span>{c.contact_name || c.phone}</span>
                  <span className={`mode-tag ${c.ai_mode}`}>{modeLabel(c.ai_mode)}</span>
                </div>
                <div className="preview">{c.service_interest || "-"}</div>
                <div className="preview">{c.last_message_preview || "Sin mensajes todavía"}</div>
                <div className="row" style={{ marginTop: 6 }}>
                  {c.temperature === "hot" && <span className="badge hot">🔥 Alta</span>}
                  {c.unread_count > 0 && <span className="badge unread">{c.unread_count}</span>}
                  {c.handoff_reason && <span className="badge">{c.handoff_reason}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Middle: thread */}
        <div className="inbox-col">
          {!detail && (
            <div className="col-empty" style={{ padding: 24 }}>
              {loadingDetail ? "Cargando conversación..." : "Elegí una conversación de la izquierda."}
            </div>
          )}
          {detail && (
            <>
              <div className="chat-header">
                <b>{detail.contact_name || detail.phone}</b>
                <div className="meta">
                  {detail.phone} · {detail.opportunity?.service_name || detail.opportunity?.service_code || "Sin servicio"}
                </div>
              </div>
              <div className="chat-msgs">
                {detail.messages.length === 0 && <div className="meta">Sin mensajes todavía.</div>}
                {detail.messages.map((m) => (
                  <div key={m.id} className={`bubble ${m.direction === "outbound" ? "outbound" : ""} ${m.ai_generated ? "ai" : ""}`}>
                    {m.ai_generated && <div className="bubble-tag">IA</div>}
                    <div>{m.body}</div>
                    <div className="bubble-meta">{formatDateTime(m.created_at)}</div>
                  </div>
                ))}
              </div>

              {detail.ai_mode === "assist" && draft && (
                <div className="ai-draft-banner">
                  <b>Borrador sugerido por IA</b>
                  <p style={{ margin: "6px 0" }}>{draft.text}</p>
                  <button className="btn-ghost" onClick={useDraft}>
                    Usar este borrador
                  </button>
                </div>
              )}

              <div className="compose-box">
                <textarea
                  placeholder={
                    detail.ai_mode === "auto"
                      ? "La IA está respondiendo automáticamente. Escribir acá pasará la conversación a modo humano."
                      : "Escribí tu respuesta..."
                  }
                  value={composeText}
                  onChange={(e: any) => setComposeText(e.target.value)}
                />
                <button className="btn-primary" onClick={sendMessage} disabled={sending || !composeText.trim()}>
                  Enviar
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right rail */}
        <div className="inbox-col">
          <div className="inbox-scroll" style={{ padding: 14 }}>
            {!detail && <p className="meta">Sin conversación seleccionada.</p>}
            {detail && (
              <>
                <h3>Resumen IA</h3>
                {detail.qualification ? (
                  <div>
                    <div className="kv">Interés: {detail.qualification.treatment_interest || "-"}</div>
                    <div className="kv">Zona: {detail.qualification.concern_area || "-"}</div>
                    <div className="kv">
                      Intención:{" "}
                      <b className={detail.qualification.intent_level === "high" ? "overdue" : ""}>
                        {detail.qualification.intent_level || "-"}
                      </b>
                    </div>
                    <div className="kv">Urgencia: {detail.qualification.urgency || "-"}</div>
                    <div className="kv">Financiación: {detail.qualification.financing_interest ? "Sí" : "No definido"}</div>
                  </div>
                ) : (
                  <p className="meta">Sin calificación de IA todavía.</p>
                )}

                {detail.pending_handoff && (
                  <div className="handoff-banner">
                    <b>Handoff pendiente ({detail.pending_handoff.reason_code})</b>
                    <p style={{ margin: "6px 0" }}>{detail.pending_handoff.summary}</p>
                    <button className="btn-primary" onClick={acceptHandoff}>
                      Aceptar
                    </button>
                  </div>
                )}

                <h3>Modo de conversación</h3>
                <div className="mode-switch">
                  {MODES.map((m) => (
                    <button
                      key={m.key}
                      className={`mode-pill ${detail.ai_mode === m.key ? "on" : ""}`}
                      onClick={() => changeMode(m.key)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                <h3>Kanban</h3>
                {detail.opportunity ? (
                  <div>
                    <div className="kv">Etapa: {detail.opportunity.stage_name || detail.opportunity.stage_code || "-"}</div>
                    <div className="kv">Responsable: {detail.opportunity.owner_name || "Sin asignar"}</div>
                    <div className="kv">Próxima acción: {detail.opportunity.next_action_text || "-"}</div>
                  </div>
                ) : (
                  <p className="meta">Sin oportunidad vinculada.</p>
                )}

                {isDevToolVisible && (
                  <div className="dev-tool">
                    <div className="tag">Herramienta de demo</div>
                    <p className="meta" style={{ marginTop: 4 }}>
                      Simular mensaje entrante (no es una función real, sólo para demos sin WhatsApp conectado).
                    </p>
                    <input
                      placeholder="Teléfono (ej: +54911...)"
                      value={devPhone}
                      onChange={(e: any) => setDevPhone(e.target.value)}
                      style={{ marginBottom: 6 }}
                    />
                    <textarea
                      placeholder="Texto del mensaje entrante"
                      value={devText}
                      onChange={(e: any) => setDevText(e.target.value)}
                      rows={2}
                      style={{ width: "100%", marginBottom: 6 }}
                    />
                    <button className="btn-ghost" onClick={simulateInbound}>
                      Simular mensaje entrante
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function modeLabel(mode: AiMode): string {
  if (mode === "auto") return "Auto IA";
  if (mode === "assist") return "Copilot";
  return "Humano";
}
